import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./EnterpriseSecurity.css";
import Testimonial from "../../../Testimonial/Testimonial";

const EnterpriseSecurity = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    // Set initial container width
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1; // Adjust speed as needed (pixels per frame)

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      <Helmet>
        <title>
          Enterprise Security Solutions | Comprehensive Cybersecurity Protection
        </title>
        <meta
          name="description"
          content="End-to-end enterprise security solutions including threat detection, data protection, network security, and compliance management for robust organizational cybersecurity."
        />
        <meta
          name="keywords"
          content="enterprise security, cybersecurity solutions, data protection, network security, threat detection, vulnerability management, security compliance, endpoint protection, cloud security, identity management, access control, encryption solutions, SIEM, security information and event management, firewall protection, intrusion detection, penetration testing, security operations center, SOC, zero trust security, data loss prevention, DLP, ransomware protection, security risk assessment, compliance auditing, GDPR compliance, HIPAA security, PCI DSS, security policy management, security awareness training, multi-factor authentication, MFA, privileged access management, PAM, security architecture, application security, API security, IoT security, blockchain security, AI security, security monitoring, incident response, threat intelligence, security automation, security governance, cyber resilience, business continuity, disaster recovery, security consulting, managed security services, security as a service"
        />
      </Helmet>

      <div className="mk-esecurity-container">
        <section className="mk-esecurity-hero">
          <div className="mk-esecurity-hero-content">
            <h1 className="mk-esecurity-title mk-esecurity-slide-in">
              Enterprise IT Security Solutions
            </h1>
            <p className="mk-esecurity-subtitle mk-esecurity-fade-in">
              Protecting your digital assets with cutting-edge security
              infrastructure
            </p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20would%20like%20to%20request%20an%20Enterprise%20IT%20Security%20Assessment%20covering%20threat%20detection%2C%20vulnerability%20analysis%2C%20and%20compliance.%20Please%20connect%20me%20with%20your%20security%20experts."
              target="_blank"
              rel="noreferrer"
            >
              <button className="mk-esecurity-cta-btn mk-esecurity-pulse">
                Get Security Assessment
              </button>
            </a>
          </div>
          <div className="mk-esecurity-hero-visual">
            <div className="mk-esecurity-shield-icon"></div>
          </div>
        </section>

        <section className="mk-esecurity-services">
          <h2 className="mk-esecurity-section-title">Our Security Services</h2>
          <div className="mk-esecurity-cards">
            <div className="mk-esecurity-card mk-esecurity-card-1">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-network-icon"></div>
              </div>
              <h3>Network Security</h3>
              <p>
                Comprehensive protection for your network infrastructure against
                intrusions and threats.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-2">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-cloud-icon"></div>
              </div>
              <h3>Cloud Security</h3>
              <p>
                Secure your cloud deployments with industry-leading practices
                and technologies.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-3">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-data-icon"></div>
              </div>
              <h3>Data Protection</h3>
              <p>
                End-to-end encryption and data loss prevention strategies for
                sensitive information.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-4">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-endpoint-icon"></div>
              </div>
              <h3>Endpoint Protection</h3>
              <p>
                Advanced security solutions for all endpoints including mobile
                devices, laptops, and workstations.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-5">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-iam-icon"></div>
              </div>
              <h3>Identity & Access Management</h3>
              <p>
                Control user access to critical systems with multi-factor
                authentication and privileged access management.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-6">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-soc-icon"></div>
              </div>
              <h3>Security Operations Center</h3>
              <p>
                24/7 monitoring, threat detection, and incident response from
                our dedicated security team.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-7">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-compliance-icon"></div>
              </div>
              <h3>Compliance & Governance</h3>
              <p>
                Ensure regulatory compliance with frameworks like GDPR, HIPAA,
                PCI-DSS, and ISO 27001.
              </p>
            </div>

            <div className="mk-esecurity-card mk-esecurity-card-8">
              <div className="mk-esecurity-card-icon">
                <div className="mk-esecurity-consulting-icon"></div>
              </div>
              <h3>Security Consulting</h3>
              <p>
                Strategic guidance and risk assessment to build a robust
                security posture for your organization.
              </p>
            </div>
          </div>
        </section>

        <section className="mk-esecurity-approach">
          <h2 className="mk-esecurity-section-title">Our Security Approach</h2>
          <div className="mk-esecurity-steps">
            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">1</div>
              <h3>Assessment</h3>
              <p>
                Comprehensive analysis of your current security posture and
                vulnerabilities.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">2</div>
              <h3>Strategy</h3>
              <p>
                Developing a customized security roadmap aligned with your
                business objectives.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">3</div>
              <h3>Implementation</h3>
              <p>
                Deploying advanced security solutions with minimal disruption to
                operations.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">4</div>
              <h3>Testing</h3>
              <p>
                Rigorous penetration testing and vulnerability assessments to
                validate security measures.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">5</div>
              <h3>Training</h3>
              <p>
                Comprehensive security awareness programs for employees to
                recognize and respond to threats.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">6</div>
              <h3>Management</h3>
              <p>
                Continuous monitoring, updates, and improvements to your
                security infrastructure.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">7</div>
              <h3>Incident Response</h3>
              <p>
                Developing and implementing protocols for rapid response to
                security incidents.
              </p>
            </div>

            <div className="mk-esecurity-step">
              <div className="mk-esecurity-step-number">8</div>
              <h3>Optimization</h3>
              <p>
                Continuous improvement of security posture based on threat
                intelligence and evolving risks.
              </p>
            </div>
          </div>
        </section>

        <section className="mk-esecurity-stats">
          <h2 className="mk-esecurity-section-title">
            Security By The Numbers
          </h2>
          <div className="mk-esecurity-statistics">
            <div className="mk-esecurity-stat">
              <div className="mk-esecurity-stat-number" data-target="99.9">
                99.9%
              </div>
              <div className="mk-esecurity-stat-label">Uptime Guarantee</div>
            </div>
            <div className="mk-esecurity-stat">
              <div className="mk-esecurity-stat-number" data-target="500">
                500
              </div>
              <div className="mk-esecurity-stat-label">Enterprise Clients</div>
            </div>
            <div className="mk-esecurity-stat">
              <div className="mk-esecurity-stat-number" data-target="24">
                24
              </div>
              <div className="mk-esecurity-stat-label">
                Response Time (hours)
              </div>
            </div>
            <div className="mk-esecurity-stat">
              <div className="mk-esecurity-stat-number" data-target="100">
                100
              </div>
              <div className="mk-esecurity-stat-label">Security Experts</div>
            </div>
          </div>
        </section>

        <section className="mk-esecurity-cta">
          <h2>Ready to Secure Your Enterprise?</h2>
          <p>
            Contact us today for a comprehensive security assessment and
            customized solution.
          </p>
          <a
            href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20Enterprise%20IT%20Security%20services%20such%20as%20threat%20monitoring%2C%20risk%20assessment%2C%20and%20compliance.%20Please%20connect%20me%20with%20your%20security%20team."
            target="_blank"
            rel="noreferrer"
          >
            <button className="mk-esecurity-contact-btn">
              Contact Our Security Team
            </button>
          </a>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseSecurity;
